import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ComponentId } from '../../types/quoter/notifications';
import logger from '../../utils/logging/logger';

const componentName = 'VersionContext';

// Define a unique namespace to avoid any potential conflicts
const VERSION_NAMESPACE = '__POINTSAILS_VERSION_SYSTEM__';

// Add at the top of both VersionContext.tsx and useComponentVersion.ts
console.log('VERSION_NAMESPACE value:', VERSION_NAMESPACE);

console.warn('VersionProvider instance being created or re-rendered');

// DIRECT GLOBAL STATE - using namespaced globals for better isolation
declare global {
    interface Window {
        [VERSION_NAMESPACE]?: {
            masterVersion: number;
            componentVersions: Record<string, number>;
            initialized: boolean;
        };
    }
}

// Initialize our versioning system with defensive programming
function initializeVersionSystem() {
    if (!window[VERSION_NAMESPACE]) {
        window[VERSION_NAMESPACE] = {
            masterVersion: 0,
            componentVersions: {},
            initialized: true
        };

        logger.log(`LogVersionUpdates`, componentName, `Version system initialized in global scope`);
    }
    return window[VERSION_NAMESPACE]!;
}

// Initialize immediately
const versionSystem = initializeVersionSystem();

// Log the initial state for debugging
logger.log(`LogVersionUpdates`, componentName, `Initial version state: master=${versionSystem.masterVersion}, components=`, { ...versionSystem.componentVersions });

interface VersionContextType {
    masterVersion: number;
    componentVersions: Record<string, number>;
    updateMasterVersion: (request: { requestingComponent: ComponentId, currentVersion: number }) => void;
    setComponentVersion: (componentId: ComponentId, version: number) => void;
    getComponentVersion: (componentId: ComponentId) => number;
}

const VersionContext = createContext<VersionContextType | undefined>(undefined);

export const VersionProvider: React.FC<{
    children: React.ReactNode;
    initialMasterVersion?: number;
}> = ({ children, initialMasterVersion = -1 }) => {


    // Ensure our version system exists on every render
    const system = window[VERSION_NAMESPACE] || initializeVersionSystem();

    // Initialize state from global system
    const [masterVersion, setMasterVersion] = useState(() => {
        if (initialMasterVersion > -1) {
            system.masterVersion = initialMasterVersion;
        } else if (system.masterVersion <= 0) {
            system.masterVersion = 0;
        }
        return system.masterVersion;
    });

    // Simple re-render trigger
    const [renderCount, setRenderCount] = useState(0);
    const forceRender = useCallback(() => {
        setRenderCount(prev => prev + 1);
    }, []);

    // Direct getter that always uses global state
    const getComponentVersion = useCallback((componentId: ComponentId) => {

        // Explicitly check window global first for reliability
        if (window[VERSION_NAMESPACE] &&
            window[VERSION_NAMESPACE].componentVersions &&
            window[VERSION_NAMESPACE].componentVersions[componentId] !== undefined) {
            return window[VERSION_NAMESPACE].componentVersions[componentId];
        }

        // Fall back to regular retrieval
        const version = window[VERSION_NAMESPACE]?.componentVersions[componentId] || -1;
        logger.log(`LogVersionUpdates`, componentName, `Getting version for ${componentId}:`, version);
        return version;
    }, []);

    // Direct setter that ensures global state persistence
    const setComponentVersion = useCallback((componentId: ComponentId, version: number): void => {
        // Always ensure our system exists first (defensive)
        const system = window[VERSION_NAMESPACE] || initializeVersionSystem();

        logger.log(`LogVersionUpdates`, componentName, `Setting ${componentId} version to`, version);
        logger.log(`LogVersionUpdates`, componentName, `Current versions before update:`, { ...system.componentVersions });

        // Simple direct assignment to global
        system.componentVersions[componentId] = version;

        // Force re-render to update consumers
        forceRender();

        logger.log(`LogVersionUpdates`, componentName, `New versions after update:`, { ...system.componentVersions });

        // Verify the change with a separate direct access
        setTimeout(() => {
            // Re-access to ensure persistence
            const currentSystem = window[VERSION_NAMESPACE];
            if (!currentSystem) {
                logger.log(`LogVersionUpdates`, componentName, `ERROR: Version system missing after update!`);
                return;
            }

            const currentVer = currentSystem.componentVersions[componentId];
            if (currentVer !== version) {
                logger.log(`LogVersionUpdates`, componentName, `WARNING: Version not set correctly for ${componentId}, expected ${version}, got ${currentVer}`);
            }
        }, 0);
    }, [forceRender]);

    // Update master version directly in global state
    const updateMasterVersion = useCallback((request: {
        requestingComponent: ComponentId,
        currentVersion: number
    }): void => {
        // Always ensure our system exists first (defensive)
        const system = window[VERSION_NAMESPACE] || initializeVersionSystem();

        const newVersion = system.masterVersion + 1;
        logger.log(`LogVersionUpdates`, componentName, `Updating master version to ${newVersion}, requested by:`, request.requestingComponent);

        // Update global
        system.masterVersion = newVersion;

        // Update local state
        setMasterVersion(newVersion);
    }, []);

    // Keep master version in sync with React state
    useEffect(() => {
        const system = window[VERSION_NAMESPACE] || initializeVersionSystem();
        system.masterVersion = masterVersion;
    }, [masterVersion]);

    // Log current state on each render for debugging
    useEffect(() => {
        const system = window[VERSION_NAMESPACE] || initializeVersionSystem();
        logger.log(`LogVersionUpdates`, componentName, `VersionContext render: masterVersion=${masterVersion}, renderCount=${renderCount}, globalMaster=${system.masterVersion}`);
    }, [masterVersion, renderCount]);

    // Debug log of component versions on render
    useEffect(() => {
        const system = window[VERSION_NAMESPACE] || initializeVersionSystem();
        logger.log(`LogVersionUpdates`, componentName, `Component versions from global:`, { ...system.componentVersions });
    });

    // Create the context value with direct global state access
    const contextValue: VersionContextType = {
        get masterVersion() {
            // Always return the current global value
            return window[VERSION_NAMESPACE]?.masterVersion || 0;
        },
        get componentVersions() {
            // Always return the current global values
            return { ...(window[VERSION_NAMESPACE]?.componentVersions || {}) };
        },
        updateMasterVersion,
        setComponentVersion,
        getComponentVersion
    };

    return (
        <VersionContext.Provider value={contextValue}>
            {children}
        </VersionContext.Provider>
    );
};

// Hook to use version state
export const useVersionState = () => {
    const context = useContext(VersionContext);
    if (!context) {
        throw new Error('useVersionState must be used within a VersionProvider');
    }
    return context;
};